import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';

import {
  fetchOpportunities,
  selectAllOpportunities,
  getOpportunitiesStatus,
  getOpportunitiesError,
} from '../opportunities/opportunitiesSlice';

import SingleSlide from '../../components/contentElements/SingleSlide';
import Blockquote from '../../components/contentElements/Blockquote';

import './HomesAnnouncesList.css';


const HomesAnnouncesList = () => {
  const dispatch = useDispatch();
  // const { i18n } = useTranslation();
  const { language } = useLanguage();

  const opportunities = useSelector(selectAllOpportunities);
  const status = useSelector(getOpportunitiesStatus);
  const error = useSelector(getOpportunitiesError);

  const [activeType, setActiveType] = useState('all');

  
  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchOpportunities());
    }
  }, [status, dispatch]);


  const getTitle = (opportunity) => {
    if (language === 'fr') {
      return (
        opportunity.title_fr ||
        opportunity.title_en ||
        ''
      );
    }

    return (
      opportunity.title_en ||
      opportunity.title_fr ||
      ''
    );
  };


  const getSummary = (opportunity) => {
    if (language === 'fr') {
      return (
        opportunity.summary_fr ||
        opportunity.summary_en ||
        ''
      );
    }

    return (
      opportunity.summary_en ||
      opportunity.summary_fr ||
      ''
    );
  };


  const typeLabels = {
    grant: {
      fr: 'Financement',
      en: 'Grant',
    },

    accelerator: {
      fr: 'Accélérateur',
      en: 'Accelerator',
    },

    investment: {
      fr: 'Investissement',
      en: 'Investment',
    },

    training: {
      fr: 'Formation',
      en: 'Training',
    },

    market: {
      fr: 'Marché',
      en: 'Market',
    },

    procurement: {
      fr: 'Marchés publics',
      en: 'Procurement',
    },

    job: {
      fr: 'Emploi',
      en: 'Jobs',
    },

    competition: {
      fr: 'Concours',
      en: 'Competition',
    },
  };


  const availableTypes = useMemo(() => {
    return [
      ...new Set(
        opportunities
          .map(
            (opportunity) =>
              opportunity.opportunity_type
          )
          .filter(Boolean)
      ),
    ];
  }, [opportunities]);


  const filteredOpportunities = useMemo(() => {
    if (activeType === 'all') {
      return opportunities;
    }

    return opportunities.filter(
      (opportunity) =>
        opportunity.opportunity_type ===
        activeType
    );
  }, [opportunities, activeType]);


  const formatDeadline = (deadline) => {
    if (!deadline) {
      return language === 'fr'
        ? 'Date ouverte'
        : 'Open deadline';
    }

    const date = new Date(deadline);

    return new Intl.DateTimeFormat(
      language === 'fr' ? 'fr-FR' : 'en-GB',
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }
    ).format(date);
  };


  const isClosingSoon = (deadline) => {
    if (!deadline) {
      return false;
    }

    const deadlineDate = new Date(deadline);
    const today = new Date();

    const difference =
      deadlineDate.getTime() -
      today.getTime();

    const days =
      difference /
      (1000 * 60 * 60 * 24);

    return days >= 0 && days <= 14;
  };


  const getTypeLabel = (type) => {
    const labels = typeLabels[type];

    if (!labels) {
      return type;
    }

    return labels[language];
  };


  if (status === 'loading' || status === 'idle') {
    return (
      <>
        <SingleSlide />
        <Blockquote />

        <div className="opportunities-status">
          {language === 'fr'
            ? 'Chargement des opportunités...'
            : 'Loading opportunities...'}
        </div>
      </>
    );
  }


  if (status === 'failed') {
    return (
      <>
        <SingleSlide />
        <Blockquote />

        <div className="opportunities-status opportunities-error">
          <h2>
            {language === 'fr'
              ? 'Impossible de charger les opportunités'
              : 'Unable to load opportunities'}
          </h2>

          {error && <p>{String(error)}</p>}
        </div>
      </>
    );
  }


  return (
    <>
      <SingleSlide />

      <Blockquote />

      <main className="opportunities-page">

        <header className="opportunities-introduction">

          <span className="opportunities-eyebrow">
            {language === 'fr'
              ? 'Opportunités ACE'
              : 'ACE Opportunities'}
          </span>

          <h1>
            {language === 'fr'
              ? 'Trouver des opportunités pour grandir, apprendre et se connecter'
              : 'Find Opportunities to Grow, Learn and Connect'}
          </h1>

          <div className="opportunities-title-line" />

          <p>
            {language === 'fr'
              ? "ACE sélectionne et partage des opportunités pertinentes pour les entrepreneurs, les MPME, les jeunes, les chercheurs et les acteurs de l'écosystème."
              : 'ACE curates and shares relevant opportunities for entrepreneurs, MSMEs, young people, researchers and ecosystem actors.'}
          </p>

        </header>


        <section
          className="opportunities-filters"
          aria-label={
            language === 'fr'
              ? "Filtrer les opportunités"
              : 'Filter opportunities'
          }
        >

          <button
            type="button"
            className={
              activeType === 'all'
                ? 'opportunity-filter active'
                : 'opportunity-filter'
            }
            onClick={() => setActiveType('all')}
          >
            {language === 'fr'
              ? 'Toutes'
              : 'All'}
          </button>

          {availableTypes.map((type) => (
            <button
              type="button"
              key={type}
              className={
                activeType === type
                  ? 'opportunity-filter active'
                  : 'opportunity-filter'
              }
              onClick={() => setActiveType(type)}
            >
              {getTypeLabel(type)}
            </button>
          ))}

        </section>


        {filteredOpportunities.length === 0 ? (

          <section className="opportunities-empty">

            <h2>
              {language === 'fr'
                ? 'Aucune opportunité disponible actuellement'
                : 'No opportunities available right now'}
            </h2>

            <p>
              {language === 'fr'
                ? 'De nouvelles opportunités seront publiées ici dès qu’elles auront été vérifiées par ACE.'
                : 'New opportunities will appear here once they have been reviewed by ACE.'}
            </p>

          </section>

        ) : (

          <section className="opportunities-grid">

            {filteredOpportunities.map(
              (opportunity) => {

                const destination =
                  opportunity.application_url ||
                  opportunity.source_url;

                return (
                  <article
                    key={opportunity.id}
                    className={`opportunity-card ${
                      opportunity.featured
                        ? 'opportunity-featured'
                        : ''
                    }`}
                  >

                    <div className="opportunity-card-top">

                      <div className="opportunity-badges">

                        <span className="opportunity-type">
                          {getTypeLabel(
                            opportunity.opportunity_type
                          )}
                        </span>

                        {opportunity.featured && (
                          <span className="opportunity-featured-label">
                            {language === 'fr'
                              ? 'À la une'
                              : 'Featured'}
                          </span>
                        )}

                      </div>


                      <div className="opportunity-deadline">

                        <span>
                          {language === 'fr'
                            ? 'Échéance'
                            : 'Deadline'}
                        </span>

                        <strong>
                          {formatDeadline(
                            opportunity.deadline
                          )}
                        </strong>

                        {isClosingSoon(
                          opportunity.deadline
                        ) && (
                          <small>
                            {language === 'fr'
                              ? 'Clôture prochaine'
                              : 'Closing soon'}
                          </small>
                        )}

                      </div>

                    </div>


                    <div className="opportunity-card-body">

                      <span className="opportunity-organization">
                        {opportunity.organization}
                      </span>

                      <h2>
                        {getTitle(opportunity)}
                      </h2>

                      <div className="opportunity-card-line" />

                      <p className="opportunity-summary">
                        {getSummary(opportunity)}
                      </p>


                      <dl className="opportunity-details">

                        {opportunity.target_audience && (
                          <div>
                            <dt>
                              {language === 'fr'
                                ? 'Public'
                                : 'Target'}
                            </dt>

                            <dd>
                              {opportunity.target_audience}
                            </dd>
                          </div>
                        )}

                        {opportunity.geography && (
                          <div>
                            <dt>
                              {language === 'fr'
                                ? 'Zone'
                                : 'Geography'}
                            </dt>

                            <dd>
                              {opportunity.geography}
                            </dd>
                          </div>
                        )}

                        {opportunity.source_name && (
                          <div>
                            <dt>
                              {language === 'fr'
                                ? 'Source'
                                : 'Source'}
                            </dt>

                            <dd>
                              {opportunity.source_name}
                            </dd>
                          </div>
                        )}

                      </dl>

                    </div>


                    <footer className="opportunity-card-footer">

                      {destination && (
                        <a
                          href={destination}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="opportunity-primary-link"
                        >
                          {language === 'fr'
                            ? "Voir l'opportunité originale"
                            : 'View Original Opportunity'}

                          <span aria-hidden="true">
                            →
                          </span>
                        </a>
                      )}

                      {opportunity.source_url &&
                        opportunity.application_url &&
                        opportunity.source_url !==
                          opportunity.application_url && (
                          <a
                            href={opportunity.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opportunity-source-link"
                          >
                            {language === 'fr'
                              ? 'Consulter la source'
                              : 'View source'}
                          </a>
                        )}

                    </footer>

                  </article>
                );
              }
            )}

          </section>

        )}


        <aside className="opportunities-source-note">

          <div>
            <span className="opportunities-source-label">
              {language === 'fr'
                ? 'À propos des opportunités'
                : 'About these opportunities'}
            </span>

            <h2>
              {language === 'fr'
                ? 'ACE vous connecte aux sources originales'
                : 'ACE Connects You to Original Sources'}
            </h2>
          </div>

          <p>
            {language === 'fr'
              ? "ACE sélectionne des opportunités provenant d'organisations externes et fournit des liens vers leurs annonces originales. Les conditions, délais et décisions de sélection restent sous la responsabilité des organisations qui publient ces opportunités."
              : 'ACE curates opportunities from external organizations and links to their original announcements. Eligibility requirements, deadlines and selection decisions remain under the responsibility of the organizations publishing each opportunity.'}
          </p>

        </aside>

      </main>
    </>
  );
};


export default HomesAnnouncesList;
